import React, { useState } from 'react'

import { ETaskStatus } from 'types/GlobalTask'
import { TCustomizedMetric } from 'types/GlobalMetric'
import { SxProps } from '@mui/material'

import {
  useAnalysisTypes,
  useRetiredAnalysisTypes
} from 'redux/hooks/AnalysisTypes'
import { useTasks } from 'redux/hooks/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import IconTip from 'components_new/molecules/IconTip'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SidebarDialog from 'components_new/molecules/SidebarDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'
import StatusBox from 'components_new/molecules/StatusBox'
import ValueText from 'components_new/molecules/ValueText'

import AnalysisDialog from 'components_new/organisms/dialogs/AnalysisDialog'
import AnalysesTable from 'components_new/organisms/analyses/AnalysesTable'
import MetricViewDialog from 'components_new/organisms/dialogs/MetricViewDialog'
import TasksTable from 'components_new/organisms/tasks/TasksTable'

import { getFollowedAnalysisTypes } from 'utils/analysis'

import {
  POTENTIAL_UNIT,
  ActiveCell,
  RetiredTitleCell,
  TitleCell,
  MetricCell,
  UnhandledCell
} from './Row'

interface AnalysisTypesOverviewTableProps {
  sx?: SxProps
}

const AnalysisTypesOverviewTable = (props: AnalysisTypesOverviewTableProps) => {
  const { sx } = props

  const { error, loading, analysisTypes } = useAnalysisTypes()
  const retired = useRetiredAnalysisTypes()
  const { tasks } = useTasks()
  const { t } = useTranslation()

  const openTasks = tasks.filter((item) => item.status !== ETaskStatus.DONE)
  const followed = getFollowedAnalysisTypes(analysisTypes)

  const [dialogMetric, setDialogMetric] = useState<TCustomizedMetric | null>(
    null
  )
  const [dialogAnalyses, setDialogAnalyses] = useState<string[] | null>(null)
  const [dialogTasks, setDialogTasks] = useState<string[] | null>(null)
  const [dialogAnalysisTypeId, setDialogAnalysisTypeId] = useState<
    string | null
  >(null)

  /*-- loading --*/
  if (loading) {
    return (
      <Box sx={sx}>
        <LoadingSection sx={{ height: 200, p: 2 }} />
      </Box>
    )
  }

  /*-- error --*/
  if (error) {
    return (
      <Box sx={sx}>
        <StatusBox
          allowClose={false}
          severity="error"
          title={t('results', 'error_load_title')}
        >
          {t('results', 'error_load_body')}
        </StatusBox>
      </Box>
    )
  }

  /*-- empty --*/
  if (followed.length === 0) {
    return (
      <EmptyPlaceholder
        background={true}
        body={t('results', 'no_metrics_body')}
        height={200}
        size="small"
        title={t('results', 'no_metrics_title')}
      />
    )
  }

  // What retired analysis types left counts as its own last row.
  const counted = retired.visible ? [...followed, retired] : followed
  const totalActivePotential = counted.reduce(
    (total, item) => total + item.active_potential,
    0
  )
  const totalDiscoveredPotential = counted.reduce(
    (total, item) => total + item.discovered_potential,
    0
  )

  return (
    <>
      <SimpleTable
        columns={[
          { title: t('analysis_types', 'analysis_type'), width: '0%' },
          { title: '' },
          {
            align: 'right',
            title: (
              <>
                {t('global', 'unhandled')}{' '}
                <IconTip
                  size="xsmall"
                  title={
                    <>
                      <u>{t('global', 'unhandled')}</u>{' '}
                      {t('results', 'unhandled_potential_tooltip')}
                    </>
                  }
                />
              </>
            ),
            width: '0%'
          },
          {
            align: 'right',
            title: (
              <>
                {t('global', 'active')}{' '}
                <IconTip
                  size="xsmall"
                  title={
                    <>
                      <u>{t('global', 'active')}</u>{' '}
                      {t('results', 'active_potential_tooltip')}
                    </>
                  }
                />
              </>
            ),
            width: '0%'
          },
          {
            align: 'right',
            padding: 'none',
            title: t('results', 'progress'),
            width: '128px'
          }
        ]}
        footer={{
          cells: [
            t('results', 'col_total'),
            '',
            <ValueText
              color="text.disabled"
              displayPositiveIndicator={true}
              key="discovered"
              unit={POTENTIAL_UNIT}
              value={totalDiscoveredPotential}
            />,
            <ValueText
              color="text.disabled"
              displayPositiveIndicator={true}
              key="active"
              unit={POTENTIAL_UNIT}
              value={totalActivePotential}
            />,
            ''
          ]
        }}
        hover={true}
        rows={[
          ...followed.map((item) => ({
            id: item.id,
            cells: [
              <TitleCell
                key="analysis"
                analysisType={item}
                metric={item.metric}
                onRun={setDialogAnalysisTypeId}
              />,
              '',
              <UnhandledCell
                key="unhandled"
                onViewAnalyses={setDialogAnalyses}
                potentials={item}
              />,
              <ActiveCell
                key="active"
                onViewTasks={setDialogTasks}
                potentials={item}
              />,
              item.metric ? (
                <MetricCell
                  key="trend"
                  metric={item.metric}
                  onViewMetric={setDialogMetric}
                />
              ) : (
                ''
              )
            ]
          })),
          ...(retired.visible
            ? [
                {
                  id: 'retired',
                  cells: [
                    <RetiredTitleCell key="analysis" />,
                    '',
                    <UnhandledCell
                      key="unhandled"
                      onViewAnalyses={setDialogAnalyses}
                      potentials={retired}
                    />,
                    <ActiveCell
                      key="active"
                      onViewTasks={setDialogTasks}
                      potentials={retired}
                    />,
                    ''
                  ]
                }
              ]
            : [])
        ]}
        stickyFirstColumn={true}
        sx={sx}
      />

      {/*-- dialogs --*/}
      {/*-- run --*/}
      <AnalysisDialog
        id={dialogAnalysisTypeId}
        onClose={() => setDialogAnalysisTypeId(null)}
        open={dialogAnalysisTypeId !== null}
      />

      {/*-- metric --*/}
      <MetricViewDialog
        metric={dialogMetric}
        onClose={() => setDialogMetric(null)}
        open={dialogMetric !== null}
      />

      {/*-- analyses --*/}
      <SidebarDialog
        actions={
          <Button fullWidth={true} href={'/analyses'} variant="outlined">
            {t('analyses', 'view_all_analyses')}
          </Button>
        }
        maxWidth={'lg'}
        onClose={() => setDialogAnalyses(null)}
        open={dialogAnalyses !== null}
        PaperProps={{
          sx: {
            height: 400
          }
        }}
      >
        <AnalysesTable ids={dialogAnalyses ?? []} stickyHeader={true} />
      </SidebarDialog>

      {/*-- tasks --*/}
      <SidebarDialog
        actions={
          <Button fullWidth={true} href={'/tasks'} variant="outlined">
            {t('tasks', 'view_all_tasks')}
          </Button>
        }
        disableOverflow={true}
        maxWidth={'lg'}
        onClose={() => setDialogTasks(null)}
        open={dialogTasks !== null}
        PaperProps={{
          sx: {
            height: 400
          }
        }}
      >
        <TasksTable
          items={openTasks.filter((task) => dialogTasks?.includes(task.id))}
          stickyHeader={true}
        />
      </SidebarDialog>
    </>
  )
}

export default AnalysisTypesOverviewTable
